import React, { useState, useRef, useEffect } from "react";
import { DayPicker } from "react-day-picker";
import { format, parse, isValid } from "date-fns";

// Inline styles for the calendar grid (react-day-picker v9)
const calendarStyles = `
  .rdp-root {
    --rdp-accent-color: #2563eb;
    --rdp-accent-background-color: #dbeafe;
    font-size: 0.875rem;
  }
  .rdp-month_caption {
    display: flex;
    justify-content: center;
    padding: 0.5rem 0;
    font-weight: 500;
    color: #1e293b;
  }
  .rdp-nav {
    display: flex;
    gap: 0.25rem;
    position: absolute;
    top: 0.5rem;
    width: 100%;
    justify-content: space-between;
    padding: 0 0.5rem;
  }
  .rdp-button_previous, .rdp-button_next {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.75rem;
    height: 1.75rem;
    border-radius: 0.375rem;
    border: none;
    background: transparent;
    cursor: pointer;
    color: #64748b;
  }
  .rdp-button_previous:hover, .rdp-button_next:hover {
    background: #f1f5f9;
    color: #1e293b;
  }
  .rdp-month_grid {
    border-collapse: collapse;
  }
  .rdp-weekdays {
    display: flex;
  }
  .rdp-weekday {
    width: 2.25rem;
    height: 2.25rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    font-weight: 500;
    color: #64748b;
  }
  .rdp-week {
    display: flex;
  }
  .rdp-day {
    width: 2.25rem;
    height: 2.25rem;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .rdp-day_button {
    width: 2rem;
    height: 2rem;
    border-radius: 0.375rem;
    border: none;
    background: transparent;
    cursor: pointer;
    font-size: 0.875rem;
    color: #1e293b;
  }
  .rdp-day_button:hover {
    background: #f1f5f9;
  }
  .rdp-today .rdp-day_button {
    background: #e2e8f0;
    font-weight: 600;
  }
  .rdp-selected .rdp-day_button {
    background: #2563eb;
    color: white;
  }
  .rdp-selected .rdp-day_button:hover {
    background: #1d4ed8;
  }
  .rdp-outside .rdp-day_button {
    color: #cbd5e1;
  }
  .rdp-disabled .rdp-day_button {
    color: #cbd5e1;
    cursor: not-allowed;
  }
  .rdp-chevron {
    fill: currentColor;
  }
`;

function DatePicker({ 
  name, 
  value, 
  placeholder = "Select date...",
  id,
  className = ""
}) {
  const [selectedDate, setSelectedDate] = useState(() => {
    if (value) {
      const parsed = parse(value, "yyyy-MM-dd", new Date());
      return isValid(parsed) ? parsed : undefined;
    }
    return undefined;
  });
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(() => {
    if (value) {
      const parsed = parse(value, "yyyy-MM-dd", new Date());
      return isValid(parsed) ? format(parsed, "d MMM yyyy") : "";
    }
    return "";
  });
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Inject styles once
  useEffect(() => {
    const styleId = "datepicker-styles";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = calendarStyles;
      document.head.appendChild(style);
    }
  }, []);

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close on escape
  useEffect(() => {
    function handleEscape(event) {
      if (event.key === "Escape") {
        setIsOpen(false);
        inputRef.current?.focus();
      }
    }
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }
  }, [isOpen]);

  const handleDaySelect = (date) => {
    if (date) {
      setSelectedDate(date);
      setInputValue(format(date, "d MMM yyyy"));
      setIsOpen(false);
      inputRef.current?.focus();
    }
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
    
    // Try to parse various formats
    const formats = ["d MMM yyyy", "dd/MM/yyyy", "MM/dd/yyyy", "yyyy-MM-dd"];
    for (const fmt of formats) {
      const parsed = parse(val, fmt, new Date());
      if (isValid(parsed) && parsed.getFullYear() > 1900) {
        setSelectedDate(parsed);
        return;
      }
    }
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const hiddenValue = selectedDate ? format(selectedDate, "yyyy-MM-dd") : "";

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Hidden input for form submission */}
      <input type="hidden" name={name} value={hiddenValue} />
      
      {/* Visible input */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          id={id}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={placeholder}
          className="block w-full rounded-md border-slate-200 bg-slate-50 focus:border-blue-500 focus:ring-blue-500 text-sm py-2.5 pl-3 pr-10"
          autoComplete="off"
        />
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-600"
        >
          <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
          </svg>
        </button>
      </div>

      {/* Calendar dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-1 bg-white rounded-lg shadow-lg border border-slate-200 p-3">
          <DayPicker
            mode="single"
            selected={selectedDate}
            onSelect={handleDaySelect}
            defaultMonth={selectedDate}
            showOutsideDays
          />
        </div>
      )}
    </div>
  );
}

export default DatePicker;
